/**
 * One kid-friendly fact per element, written as a full sentence for the element page's search
 * description (pageMeta.js). Separate from the descriptions in elements.js, which Fizzy reads aloud.
 */
export const ELEMENT_PAGE_FACTS = Object.freeze({
  H: 'It is the lightest element, and stars are mostly made of it.',
  He: 'It makes balloons float and voices squeaky.',
  Li: 'This soft metal powers the batteries in phones and tablets.',
  Be: 'This light, strong metal is used in space telescopes.',
  B: 'It is found in borax, which is used to make slime.',
  C: 'It is in pencils, diamonds and every living thing, including you.',
  N: 'Most of the air you breathe is nitrogen.',
  O: 'We need it to breathe, and fires need it to burn.',
  F: 'Fluoride, made from it, helps toothpaste keep teeth strong.',
  Ne: 'It makes bright signs glow red-orange.',
  Na: 'This metal is so soft you can cut it with a butter knife.',
  Mg: 'It burns with a dazzling white light in fireworks.',
  Al: 'Soda cans and kitchen foil are made of it.',
  Si: 'Sand is made from it, and so are computer chips.',
  P: 'It helps matches light when you strike them on the box.',
  S: 'This yellow element smells like rotten eggs when it reacts.',
  Cl: 'This green-yellow gas helps keep swimming pools clean.',
  Ar: 'This lazy gas fills old-style light bulbs.',
  K: 'Bananas are full of it, and the pure metal fizzes wildly in water.',
  Ca: 'It keeps your bones and teeth strong.',
  Sc: 'It makes aluminium extra strong in some bike frames and baseball bats.',
  Ti: 'This strong, light metal is used in planes and bikes.',
  V: 'Adding it to steel makes tough tools like spanners.',
  Cr: 'It makes car bumpers and taps shiny.',
  Mn: 'It makes steel tough, and it is inside many batteries.',
  Fe: 'This strong metal is used to make steel for bridges and buildings.',
  Co: 'It gives glass and paint a deep blue colour.',
  Ni: 'It is found in coins and magnets.',
  Cu: 'This orange-brown metal carries electricity through wires.',
  Zn: 'It stops steel rusting and is in some sunscreens.',
  Ga: 'This metal can melt in your hand.',
  Ge: 'It was used to make some of the very first transistors.',
  As: 'It is famous as a poison, so scientists handle it with great care.',
  Se: 'Your body needs a tiny bit of it, and it helps some solar cells work.',
  Br: 'It is one of only two elements that are liquid at room temperature.',
  Kr: "Superman's kryptonite is made up, but krypton gas is real.",
  Rb: 'This soft metal bursts into flame when it touches water.',
  Sr: 'It makes fireworks glow bright red.',
  Y: 'It helps white LED lights shine.',
  Zr: 'Sparkly fake diamonds called cubic zirconia are made from it.',
  Nb: 'It helps make the super-strong magnets in hospital MRI scanners.',
  Mo: 'It makes steel tough enough for drill bits and engine parts.',
  Tc: 'It was the first element ever made by scientists in a lab.',
  Ru: 'A tiny bit of it makes platinum jewellery harder.',
  Rh: 'It is one of the rarest and most expensive metals in the world.',
  Pd: 'It soaks up hydrogen gas like a sponge.',
  Ag: 'This shiny metal is used for jewellery and medals.',
  Cd: 'It was used in rechargeable batteries, but it is poisonous.',
  In: 'It helps the touchscreens on phones work.',
  Sn: 'Tin cans are really steel with a thin coat of it.',
  Sb: 'People in ancient Egypt used it as black eye make-up.',
  Te: 'It helps some solar panels turn sunlight into electricity.',
  I: 'It cleans cuts and scrapes, and its gas is purple.',
  Xe: 'It makes very bright car headlights.',
  Cs: 'It keeps time in super-accurate atomic clocks.',
  Ba: 'It makes fireworks glow green.',
  La: 'It is used in camera lenses and hybrid car batteries.',
  Ce: 'It makes the sparks when you strike a lighter flint.',
  Pr: "It colours glass yellow-green and protects welders' eyes.",
  Nd: 'It makes the strongest magnets in the world.',
  Pm: 'It is radioactive and so rare that almost none exists on Earth.',
  Sm: 'It makes strong magnets that keep working when they get very hot.',
  Eu: 'It makes euro banknotes glow under special lights.',
  Gd: 'It helps doctors see inside the body in MRI scans.',
  Tb: 'It helps make the green colour in screens and lamps.',
  Dy: 'It keeps the magnets in electric car motors strong when they heat up.',
  Ho: 'Its atoms are more strongly magnetic than any other element.',
  Er: 'It turns glass pink and boosts the light in internet cables.',
  Tm: 'It is one of the rarest lanthanides and is used in portable X-ray machines.',
  Yb: 'It helps make some of the most accurate clocks ever built.',
  Lu: 'It is the last and heaviest of the lanthanides.',
  Hf: 'It is used in the control rods of nuclear submarines.',
  Ta: 'It is used to make tiny parts inside phones and laptops.',
  W: 'It has the highest melting point of any metal.',
  Re: 'It is one of the rarest elements and helps jet engines handle heat.',
  Os: 'It is the densest element, much heavier than the same amount of lead.',
  Ir: 'A layer of it in rocks helped show how the dinosaurs died out.',
  Pt: 'This precious metal helps clean car exhaust.',
  Au: 'It almost never reacts, so it stays shiny forever.',
  Hg: 'It is the only metal that is liquid at room temperature.',
  Tl: 'It is very poisonous, so scientists handle it with great care.',
  Pb: 'This heavy, soft metal was used for old water pipes, but it is poisonous.',
  Bi: 'It grows rainbow-coloured crystals and is in some tummy medicines.',
  Po: 'It is very radioactive and was discovered by Marie and Pierre Curie.',
  At: 'It is one of the rarest natural elements on Earth.',
  Rn: 'This radioactive gas can seep out of the ground.',
  Fr: 'It is so radioactive that half of it vanishes in about 20 minutes.',
  Ra: 'It glows in the dark and was discovered by Marie and Pierre Curie.',
  Ac: 'It is so radioactive that it glows blue in the dark.',
  Th: 'It is radioactive and could one day fuel nuclear power stations.',
  Pa: 'It is one of the rarest and most radioactive natural elements.',
  U: 'It is radioactive and fuels nuclear power stations.',
  Np: 'It is named after the planet Neptune.',
  Pu: 'It is radioactive and powered the Curiosity rover on Mars.',
  Am: 'Tiny amounts of it are inside many smoke detectors.',
  Cm: 'It is named after the scientists Marie and Pierre Curie.',
  Bk: 'It is named after Berkeley, the city where it was first made.',
  Cf: 'It is named after California and helps find oil underground.',
  Es: 'It is named after the scientist Albert Einstein.',
  Fm: 'It is named after the scientist Enrico Fermi.',
  Md: 'It is named after Dmitri Mendeleev, who made the periodic table.',
  No: 'It is named after Alfred Nobel, who started the Nobel Prizes.',
  Lr: 'It is named after Ernest Lawrence, who invented the cyclotron.',
  Rf: "It is named after Ernest Rutherford, who found the atom's nucleus.",
  Db: 'It is named after Dubna, a Russian town famous for science.',
  Sg: 'It is named after Glenn Seaborg, who discovered many elements.',
  Bh: 'It is named after Niels Bohr, who helped explain how atoms work.',
  Hs: 'It is named after Hesse, the German state where it was made.',
  Mt: 'It is named after Lise Meitner, who helped discover nuclear fission.',
  Ds: 'It is named after Darmstadt, the German city where it was made.',
  Rg: 'It is named after Wilhelm Röntgen, who discovered X-rays.',
  Cn: 'It is named after Copernicus, who said Earth goes around the Sun.',
  Nh: 'It was made in Japan, and its name comes from Nihon, Japanese for Japan.',
  Fl: 'It is named after the Russian lab where it was first made.',
  Mc: 'It is named after Moscow, the region where it was made.',
  Lv: 'It is named after Livermore, the US lab that helped make it.',
  Ts: 'It is named after Tennessee, the US state that helped make it.',
  Og: 'It is the heaviest element ever made and lasts under a thousandth of a second.',
});
